import React, { useState } from 'react';
import type { Event } from '@/types/event';
import { imageSrc } from '@/utils/event';
import { Calendar, MapPin, Check } from 'lucide-react';
import { Modal } from '../basic/Modal';
import { Card } from '../basic/Card';
import { Button } from '../basic/Button';
import ModalHeader from './ModalHeader';

interface SuccessModalProps {
  event: Event;
  reservationData: any;
  onClose: () => void;
  quota?: any;
}

const SuccessModal: React.FC<SuccessModalProps> = ({ event, reservationData, onClose, quota }) => {
  const [myQuotas, setMyQuotasStatus] = useState<Record<string, any>>(quota);
  const eventType = event.type == "prof networking" ? 'professional_network_building' : 'career_coaching';
  const balanceQuotas = myQuotas?.summary && eventType in myQuotas?.summary ? myQuotas?.summary[eventType]?.total_remaining : 0;
  const canReserve = balanceQuotas > 0;
  const simulateQuotas = canReserve ? balanceQuotas - 1 : 0;

  return (
    <Modal onClose={onClose} size="sm">
      <div className="px-1 sm:px-2 pt-1 sm:pt-2">
        <ModalHeader event={event} onClose={onClose} />
      </div>
      <div className="px-4 sm:px-5 lg:px-6 pb-4 sm:pb-5 lg:pb-6">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 bg-green-500/20 rounded-full flex items-center justify-center">
            <Check size={24} className="sm:w-8 sm:h-8 text-emerald-500" />
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-normal text-white mb-1 sm:mb-2">
            Reservation Successful!
          </h2>
          <p className="text-white/70 text-sm sm:text-base font-normal px-2">
            Your reservation details have been sent to your email address.
          </p>
        </div>

        {/* Event Summary */}
        <Card className="p-4 sm:p-5 lg:p-6 mb-4 sm:mb-5 lg:mb-6 text-left">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-3 sm:mb-4">
            <img 
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl sm:rounded-2xl object-cover flex-shrink-0" 
              src={imageSrc(event)} 
              alt={event.title}
            />
            <div className="flex-1 text-center sm:text-left">
              <span className="inline-block bg-orange-500/40 text-white text-xs px-2 sm:px-3 py-1 rounded-full mb-1">
                {event.type?.toUpperCase() || 'WORKSHOP'}
              </span>
              <h3 className="text-lg sm:text-xl font-normal text-white mb-0.5 line-clamp-2">
                {event.title}
              </h3>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-3 sm:pt-4 space-y-2 sm:space-y-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <Calendar size={16} className="sm:w-5 sm:h-5 text-amber-500 flex-shrink-0" />
              <span className="text-white text-sm sm:text-base font-light break-words">
                {new Date(event.event_date as string).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'short', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <MapPin size={16} className="sm:w-5 sm:h-5 text-amber-500 flex-shrink-0" />
              <span className="text-white text-sm sm:text-base font-light break-words">
                {event.address?.venue || event.location}
              </span>
            </div>
          </div>
        </Card>

        {/* Quota Remaining */}
        <div className="bg-orange-500/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 border border-orange-500/30 mb-4 sm:mb-5 lg:mb-6">
          <p className="text-orange-300 text-xs sm:text-sm">
            <span className="font-medium">{simulateQuotas}</span> quota remaining in your Skillsture Plus plan
          </p>
        </div>

        {/* Done Button */}
        <Button
          onClick={onClose}
          variant="primary"
          size="lg"
          className="w-full text-sm sm:text-base"
        >
          Done
        </Button>
      </div>
    </Modal>
  );
};

export default SuccessModal;